import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Mail, Lock, UserPlus, LogIn, Building, AlertCircle, CheckCircle, Eye, EyeOff, RefreshCw } from 'lucide-react';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isSignUp, setIsSignUp] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isSignUp) {
        await handleSignUp();
      } else {
        await handleSignIn();
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async () => {
    if (!companyName.trim()) {
      throw new Error('Company name is required');
    }
    
    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }

    // Step 1: Sign up the user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: email.trim(),
      password: password.trim(),
      options: {
        data: {
          company_name: companyName.trim()
        }
      }
    });
    
    if (authError) {
      if (authError.message.includes('already registered')) {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }
      
      if (authError.message.includes('password')) {
        throw new Error('Password is too weak. Please use a stronger password.');
      }
      
      if (authError.message.includes('email')) {
        throw new Error('Please enter a valid email address.');
      }
      
      throw new Error(`Signup failed: ${authError.message}`);
    }

    if (!authData.user) {
      throw new Error('Failed to create user account. Please try again.');
    }

    // Step 2: Create company
    const { data: companyData, error: companyError } = await supabase
      .from('companies')
      .insert([
        {
          name: companyName.trim(),
          created_by: authData.user.id
        }
      ])
      .select()
      .single();

    if (companyError) {
      // Try to clean up the auth user
      try {
        await supabase.auth.signOut();
      } catch (cleanupError) {
        console.warn('Cleanup failed:', cleanupError.message);
      }
      
      if (companyError.message.includes('violates foreign key')) {
        throw new Error('Database configuration error. Please contact support.');
      } else if (companyError.message.includes('null value')) {
        throw new Error('Missing required information. Please refresh and try again.');
      } else {
        throw new Error(`Could not create company: ${companyError.message}`);
      }
    }

    // Step 3: Link user to company
    const { error: memberError } = await supabase
      .from('company_members')
      .insert([
        {
          company_id: companyData.id,
          user_id: authData.user.id,
          role: 'owner',
          joined_at: new Date().toISOString()
        }
      ]);

    if (memberError) {
      // Clean up company
      try {
        await supabase.from('companies').delete().eq('id', companyData.id);
      } catch (deleteError) {
        console.warn('Failed to clean up company:', deleteError.message);
      }
      
      throw new Error('Failed to complete account setup. Please try again.');
    }
    
    // Success!
    setSuccess('🎉 Account created successfully! You can now sign in.');
    
    // Auto-switch to sign in
    setTimeout(() => {
      setIsSignUp(false);
      setEmail('');
      setPassword('');
      setCompanyName('');
    }, 3000);
  };

  const handleSignIn = async () => {
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: password.trim(),
    });

    if (signInError) {
      if (signInError.message.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password. Please try again.');
      }
      
      throw signInError;
    }

    // Check for company
    const { data: companyCheck } = await supabase
      .from('company_members')
      .select('company_id')
      .eq('user_id', data.user.id)
      .maybeSingle();

    if (!companyCheck) {
      await createDefaultCompany(data.user);
    }

    setSuccess('✅ Signed in successfully! Redirecting...');
    
    setTimeout(() => {
      navigate('/');
    }, 1000);
  };

  const createDefaultCompany = async (user) => {
    try {
      const { data: companyData } = await supabase
        .from('companies')
        .insert([
          {
            name: `${user.email.split('@')[0]}'s Company`,
            created_by: user.id
          }
        ])
        .select()
        .single();

      await supabase
        .from('company_members')
        .insert([
          {
            company_id: companyData.id,
            user_id: user.id,
            role: 'owner'
          }
        ]);

      console.log(`Created default company: ${companyData.name}`);
    } catch (error) {
      console.warn('Failed to create default company:', error.message);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth`,
    });

    if (resetError) {
      setError(resetError.message);
    } else {
      setSuccess(`Password reset email sent to ${email}`);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-lg mb-6">
            <Building className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">JobFlow Pro</h1>
          <p className="text-gray-600">
            {isSignUp ? 'Start managing your business today' : 'Welcome back to your dashboard'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-700 font-medium">Error</p>
                  <p className="text-red-600 text-sm mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-green-700 font-medium">Success!</p>
                  <p className="text-green-600 text-sm mt-1">{success}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="you@company.com"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                  minLength="6"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {isSignUp && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Name
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your Company LLC"
                    required
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  {isSignUp ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : (
                <>
                  {isSignUp ? (
                    <>
                      <UserPlus className="w-5 h-5" />
                      Create Account
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      Sign In
                    </>
                  )}
                </>
              )}
            </button>
          </form>

          <div className="flex items-center my-8">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-gray-500 text-sm">or</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          <div className="text-center">
            <p className="text-gray-600 mb-2">
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}
            </p>
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
                setSuccess('');
              }}
              className="text-blue-600 hover:text-blue-700 font-semibold text-lg"
              disabled={loading}
            >
              {isSignUp ? 'Sign In Instead' : 'Create New Account'}
            </button>
          </div>

          {!isSignUp && (
            <div className="mt-6 text-center">
              <button
                onClick={handlePasswordReset}
                className="text-gray-500 hover:text-gray-700 text-sm"
                disabled={loading}
              >
                Forgot your password?
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;